import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CTASection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="cta" data-testid="cta-section" className="py-[45px] md:py-[90px] relative overflow-hidden" style={{ background: '#0A2540' }}>
      {/* Gradient orbs */}
      <div className="absolute top-0 left-[20%] w-[600px] h-[600px] rounded-full bg-stripe-primary/10 blur-[150px]" />
      <div className="absolute bottom-0 right-[10%] w-[400px] h-[400px] rounded-full bg-cyan-400/8 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 max-w-md">
              Ready to transform{" "}
              <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">
                your team's skills?
              </span>
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8">
              Get early access to SkillsimAI and be among the first to experience AI-powered
              soft skills training. Limited spots available.
            </p>
            <div className="flex flex-col gap-3 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Free trial for early adopters
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Priority onboarding for teams
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Shape the product with your feedback
              </span>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {submitted ? (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center" data-testid="form-success-message">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  You're on the list!
                </h3>
                <p className="text-sm text-slate-400">
                  We'll be in touch soon with your early access invite.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 space-y-4"
                data-testid="early-access-form"
              >
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  Request Early Access
                </h3>
                <Input
                  data-testid="form-name-input"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-stripe-primary focus:ring-stripe-primary/20 rounded-xl"
                />
                <Input
                  data-testid="form-email-input"
                  type="email"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-stripe-primary focus:ring-stripe-primary/20 rounded-xl"
                />
                <Input
                  data-testid="form-company-input"
                  placeholder="Company (optional)"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-stripe-primary focus:ring-stripe-primary/20 rounded-xl"
                />
                <textarea
                  data-testid="form-message-input"
                  placeholder="Message (optional)"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 text-white placeholder:text-slate-500 focus:border-stripe-primary focus:outline-none focus:ring-2 focus:ring-stripe-primary/20 rounded-xl resize-none"
                />
                {error && <p className="text-xs text-red-400" data-testid="form-error">{error}</p>}
                <Button
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={submitting}
                  className="w-full bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-full py-6 text-base font-medium shadow-lg shadow-stripe-primary/25 hover:shadow-xl hover:shadow-stripe-primary/30 transition-all duration-300 group"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Request Access
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
